import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { useGetWidgetAnalysisConfig } from "@/api/widget/useWidgetAPI";
import { CloseIcon } from "@/assets/icons";
import Modal from "@/components/ui/modal";
import { MESSAGE } from "@/constants/message";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useToast from "@/hooks/ui/useToast";
import { RootState } from "@/store";
import { addWidget } from "@/store/slice/widgetSlice";
import {
  AggregationType,
  OrderByType,
  PostWidgetAnalysisResponse,
  WidgetPreviewData,
  WidgetType
} from "@/types/widget";
import { mapPostWidgetAnalysisConfigPayload } from "@/utils/widget";

import FormWidgetConfiguration, {
  placeHolderMap
} from "./FormWidgetConfiguration";
import WidgetConfigurationTab from "./WidgetConfigurationTab";

export interface WidgetFormData {
  chart_type: WidgetType;
  label: string;
  dataset_id: string;
  dimension: string;
  measure: string;
  aggregation: AggregationType;
  configuration: {
    order_by: OrderByType;
    dimension_values_shown: number;
  };
}

interface WidgetConfigurationProps {
  open: boolean;
  onClose: () => void;
  /** True when the modal is opened to configure an existing widget; true locks the chart-type tabs so the user cannot switch chart types mid-edit. */
  isEditing: boolean;
}

const DEFAULT_FORM_VALUES: WidgetFormData = {
  chart_type: WidgetType.BAR_V,
  label: "",
  dataset_id: "",
  dimension: "",
  measure: "",
  aggregation: AggregationType.COUNT,
  configuration: {
    order_by: OrderByType.DESC,
    dimension_values_shown: 10
  }
};

const WidgetConfiguration = ({
  open,
  onClose,
  isEditing
}: WidgetConfigurationProps) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { showConfirmation } = useConfirmationModal();

  const method = useForm<WidgetFormData>({
    defaultValues: DEFAULT_FORM_VALUES
  });
  const { filters } = useSelector((state: RootState) => state.dataset);

  const [previewData, setPreviewData] = useState<WidgetPreviewData>([]);
  const [queryState, setQueryState] = useState<{
    enabled: boolean;
    trigger: "preview" | "submit";
  }>({ enabled: false, trigger: "preview" });

  const {
    watch,
    setValue,
    reset,
    handleSubmit,
    formState: { dirtyFields }
  } = method;

  const watchChartType = watch("chart_type");
  // Subscribe to all fields so the query payload stays in sync with the latest
  // form input.
  const formValue = watch();

  const {
    isFetching: isFetchingWidgetAnalysis,
    data: widgetAnalysisData,
    isError: isErrorWidgetAnalysis
  } = useGetWidgetAnalysisConfig(
    mapPostWidgetAnalysisConfigPayload(
      formValue,
      filters[formValue.dataset_id] ?? []
    ),
    { enabled: queryState.enabled }
  );

  const applyPreviewFromResponse = useCallback(
    (response: PostWidgetAnalysisResponse) => {
      if (response.data?.value) {
        setPreviewData([
          {
            type:
              (response.data.aggregation as AggregationType) ??
              AggregationType.COUNT,
            value: response.data.value,
            measure: response.data.measure || ""
          }
        ]);
        return;
      }
      const preview = response.data?.data || response.data?.rows;
      setPreviewData(
        preview?.slice(0, formValue.configuration.dimension_values_shown) || []
      );
    },
    [formValue.configuration.dimension_values_shown]
  );

  const handleClickPreview = () => {
    setQueryState({ enabled: true, trigger: "preview" });
  };

  const onSubmit = () => {
    setQueryState({ enabled: true, trigger: "submit" });
  };

  useEffect(() => {
    if (!queryState.enabled || isFetchingWidgetAnalysis) return;
    if (!widgetAnalysisData && !isErrorWidgetAnalysis) return;

    if (isErrorWidgetAnalysis || !widgetAnalysisData) {
      showToast({
        message:
          queryState.trigger === "preview"
            ? MESSAGE.WIDGET_FAILED_TO_PREVIEW_DESCRIPTION
            : MESSAGE.WIDGET_FAILED_TO_ADD_DESCRIPTION,
        type: "error"
      });
    } else if (queryState.trigger === "preview") {
      applyPreviewFromResponse(widgetAnalysisData);
    } else {
      dispatch(
        addWidget({
          id: formValue.dataset_id,
          widget: formValue
        })
      );
      setPreviewData([]);
      onClose();
      showToast({
        title: MESSAGE.WIDGET_SUCCESS_ADDED_TITLE,
        message: MESSAGE.WIDGET_SUCCESS_ADDED_DESCRIPTION,
        type: "success"
      });
    }

    setQueryState((prev) => ({ ...prev, enabled: false }));
  }, [
    queryState.enabled,
    isFetchingWidgetAnalysis,
    isErrorWidgetAnalysis,
    widgetAnalysisData,
    queryState.trigger,
    showToast,
    applyPreviewFromResponse,
    dispatch,
    formValue,
    onClose
  ]);

  const handleConfirmClose = useCallback(() => {
    reset(DEFAULT_FORM_VALUES);
    onClose();
    setPreviewData([]);
  }, [onClose, reset]);

  const handleClose = useCallback(() => {
    if (Object.keys(dirtyFields).length) {
      showConfirmation({
        title: "Unsaved Changes",
        message: MESSAGE.CONFIRMATION_LEAVE,
        cancelLabel: "Cancel",
        confirmLabel: "Leave",
        onConfirm: handleConfirmClose
      });
    } else handleConfirmClose();
  }, [showConfirmation, dirtyFields, handleConfirmClose]);

  // Reset stale form state when the modal is reopened so the previous session's
  // partial input does not leak into the next "Add" or "Edit" flow.
  useEffect(() => {
    if (open) reset(DEFAULT_FORM_VALUES);
  }, [open, reset]);

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      shouldCloseModalOnClickOutside={false}
    >
      <div className="w-[80vw] h-[90vh] bg-white p-4 rounded-lg">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-txtHeading">
            Widget Configuration
          </h2>
          <button onClick={handleClose}>
            <CloseIcon />
          </button>
        </div>

        <FormProvider {...method}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex gap-2 mt-4 h-[calc(90vh-80px)]"
          >
            <FormWidgetConfiguration
              widgetType={watchChartType}
              onClickPreview={handleClickPreview}
            />
            <WidgetConfigurationTab
              widgetType={watchChartType}
              setWidgetType={(type) => {
                setValue("chart_type", type);

                const newPlaceholders = placeHolderMap[type];
                if (!newPlaceholders.dimension) {
                  setValue("dimension", DEFAULT_FORM_VALUES.dimension);
                }
                if (!newPlaceholders.measure) {
                  setValue("measure", DEFAULT_FORM_VALUES.measure);
                }
                if (!newPlaceholders.dimension_values_shown) {
                  setValue(
                    "configuration.dimension_values_shown",
                    DEFAULT_FORM_VALUES.configuration.dimension_values_shown
                  );
                }
                if (!newPlaceholders.group_by) {
                  setValue(
                    "configuration.order_by",
                    DEFAULT_FORM_VALUES.configuration.order_by
                  );
                }

                setPreviewData([]);
              }}
              isEditing={isEditing}
              isLoading={isFetchingWidgetAnalysis}
              previewData={previewData}
            />
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
};

export default WidgetConfiguration;
